// @ts-check

/// <reference path="ata.js" />
/// <reference path="image-builder.js" />

/** @typedef {{ id: number; name: string; description: string; image: string; ord_code: string; display_color: string; filter: string; defaultValue: boolean }} FabricColor */
/** @typedef {{ id: number; name: string; description: string; image: string; ord_code: string; width: string; defaultValue: boolean; colors: FabricColor[] }} ModelFabric */

class FabricColorSelection extends HTMLElement {
  constructor() {
    super();

    /** @type {ShirtImageBuilder | null} */
    this.imageBuilder = document.querySelector("shirt-image-builder");

    this.fabricRefs = {
      /** @type {() => HTMLTemplateElement | null} */
      inputTemplate: () => this.querySelector("[data-fabric-input-template]"),
      /** @type {() => NodeListOf<HTMLInputElement>} */
      radioInputs: () => this.querySelectorAll("[data-fabric-input]"),
      /** @type {() => HTMLUListElement | null} */
      optionsList: () => this.querySelector("#fabric-selection-options"),
      /** @type {() => HTMLDivElement | null} */
      previewContainer: () => this.querySelector("#fabric-preview"),
      /** @type {() => HTMLDivElement | null} */
      descriptionContainer: () => this.querySelector("#fabric-description"),
    };

    this.colorRefs = {
      /** @type {() => HTMLTemplateElement | null} */
      inputTemplate: () => this.querySelector("[data-color-input-template]"),
      /** @type {() => NodeListOf<HTMLInputElement>} */
      radioInputs: () => this.querySelectorAll("[data-color-input]"),
      /** @type {() => HTMLUListElement | null} */
      optionsList: () => this.querySelector("#color-selection-options"),
      /** @type {() => HTMLDivElement | null} */
      previewContainer: () => this.querySelector("#color-preview"),
      /** @type {() => HTMLDivElement | null} */
      nameContainer: () => this.querySelector("#color-name"),
      /** @type {() => HTMLDivElement | null} */
      descriptionContainer: () => this.querySelector("#color-description"),
      /** @type {() => HTMLInputElement | null} */
      colorCode: () =>
        this.querySelector('input[name="properties[_colorCode]"]'),
      /** @type {() => HTMLInputElement | null} */
      colorFilter: () =>
        this.querySelector('input[name="properties[_colorFilter]"]'),
    };

    this.abortController = new AbortController();

    /** @type {PromiseWithResolvers<boolean>} */
    this.readyPromise = Promise.withResolvers();

    /** @type {Map<'fabric' | 'color', string>} */
    this.currentValues = new Map();
  }

  connectedCallback() {
    const modelName = this.getAttribute("model");
    if (!modelName) {
      throw new Error("Model name not provided");
    }

    Promise.all([
      this.initializeFabrics(modelName),
      customElements.whenDefined("shirt-image-builder"),
    ])
      .then(([{ fabrics }]) => {
        this.fabricsData = fabrics;
        this.setDefaultValues();
        this.showFabrics(fabrics).then(() => {
          this.readyPromise.resolve(true);
        });
      })
      .catch((err) => {
        this.handleError(err);
      });
  }

  connectedMoveCallback() {
    /**
     * Intentionally empty
     * @see https://developer.mozilla.org/en-US/docs/Web/API/Web_components/Using_custom_elements#lifecycle_callbacks_and_state-preserving_moves
     */
  }

  disconnectedCallback() {
    this.abortController.abort();
    this.readyPromise.reject();
  }

  /**
   * @param {ModelFabric} modelFabric
   */
  async setFabric(modelFabric) {
    this.selectVariant("fabric", modelFabric.name.toUpperCase());
    this.updateState("fabric", modelFabric.name.toUpperCase());
    this.setFabricImage(modelFabric);
    this.setFabricDescription(modelFabric);
    await this.showColors(modelFabric.colors);
  }

  /**
   * @param {FabricColor} fabricColor
   */
  async setColor(fabricColor) {
    this.selectVariant("color", fabricColor.name.toUpperCase());
    this.updateState("color", fabricColor.name.toUpperCase());
    this.setColorHiddenProperties(fabricColor);
    this.setColorImage(fabricColor);
    this.setColorName(fabricColor);
    this.setColorDescription(fabricColor);
    await this.imageBuilder?.setImage();
  }

  /**
   * @param {string} modelName
   * @returns {Promise<{fabrics: ModelFabric[]}>}
   */
  async initializeFabrics(modelName) {
    const signal = AbortSignal.any([
      this.abortController.signal,
      AbortSignal.timeout(30000),
    ]);
    const response = await fetch(`${apiEndpoint}/model-fabrics/${modelName}`, {
      signal,
    });
    if (!response.ok) {
      throw new Error(`Network response error: ${response.statusText}`);
    }
    return await response.json();
  }

  /**
   * @param {string | undefined | null} fabricName
   * @returns {ModelFabric}
   */
  validateFabric(fabricName) {
    if (!fabricName) {
      throw new Error("No fabric selected");
    }
    const fabricFromDb = this.fabricsData?.find(({ name }) =>
      compareStringsCaseInsensitive(name, fabricName)
    );
    if (!fabricFromDb) {
      throw new Error("Invalid fabric");
    }
    return fabricFromDb;
  }

  /**
   * @param {ModelFabric} modelFabric
   * @param {string | undefined | null} colorName
   * @returns {FabricColor}
   */
  validateColor(modelFabric, colorName) {
    const colorFromDb = modelFabric.colors.find(({ name }) =>
      compareStringsCaseInsensitive(name, colorName)
    );
    if (!colorFromDb) {
      throw new Error("Invalid color");
    }
    return colorFromDb;
  }

  /**
   * @param {ModelFabric[]} modelFabrics
   */
  async showFabrics(modelFabrics) {
    const ul = this.fabricRefs.optionsList();
    const template = this.fabricRefs.inputTemplate();
    if (!ul || !template) {
      throw new Error("Fabric options list or template not found");
    }
    for (const modelFabric of modelFabrics) {
      const li = /** @type {DocumentFragment} */ (
        template.content.cloneNode(true)
      );
      const label = li.querySelector("label");
      const input = li.querySelector("input");
      if (!label || !input) {
        throw new Error("Invalid template");
      }
      label.append(modelFabric.name);
      const upperValue = modelFabric.name.toUpperCase();
      input.value = upperValue;
      input.addEventListener("change", async () => {
        await this.setFabric(modelFabric);
      });

      if (this.defaultValues) {
        if (this.defaultValues.fabric === upperValue) {
          input.checked = true;
          await this.setFabric(modelFabric);
        }
      } else if (modelFabric.defaultValue) {
        input.checked = true;
        await this.setFabric(modelFabric);
      }

      ul.appendChild(li);
    }
  }

  /**
   * @param {FabricColor[]} fabricColors
   */
  async showColors(fabricColors) {
    const ul = this.colorRefs.optionsList();
    const template = this.colorRefs.inputTemplate();
    if (!ul || !template) {
      throw new Error("Color options list or template not found");
    }
    ul.replaceChildren();

    /** @type {FabricColor | null} */
    let firstFabric = null;
    /** @type {HTMLInputElement | null} */
    let firstInput = null;
    let isSet = false;

    for (const fabricColor of fabricColors) {
      const li = /** @type {DocumentFragment} */ (
        template.content.cloneNode(true)
      );
      /** @type {HTMLSpanElement | null} */
      const labelText = li.querySelector("label > span");
      const input = li.querySelector("input");
      if (!labelText || !input) {
        throw new Error("Invalid template");
      }
      labelText.style.setProperty(
        "background-color",
        fabricColor.display_color
      );
      const upperValue = fabricColor.name.toUpperCase();
      input.value = upperValue;
      input.addEventListener("change", async () => {
        await this.setColor(fabricColor);
      });

      if (this.defaultValues) {
        if (this.defaultValues.color === upperValue) {
          input.checked = true;
          await this.setColor(fabricColor);
          isSet = true;
        }
      } else if (fabricColor.defaultValue) {
        input.checked = true;
        await this.setColor(fabricColor);
        isSet = true;
      }

      if (!firstFabric) {
        firstFabric = fabricColor;
        firstInput = input;
      }

      ul.appendChild(li);
    }

    if (!isSet && firstInput && firstFabric) {
      firstInput.checked = true;
      await this.setColor(firstFabric);
      isSet = true;
    }
  }

  /**
   * @param {'fabric' | 'color'} attr
   * @param {string} value
   */
  updateState(attr, value) {
    this.currentValues.set(attr, value);
    updateURLSearchParams({ [attr]: value });
  }

  /**
   * @param {'fabric' | 'color'} option
   * @param {string} value
   */
  selectVariant(option, value) {
    /** @type {HTMLInputElement | null} */
    const input = document.querySelector(
      `variant-selects input[name*="${option}" i][value="${value}" i]`
    );
    if (input) {
      input.click();
    } else {
      console.debug(
        "Unknown variant. Data in the database and shopify products don't match."
      );
    }
  }

  setDefaultValues() {
    const savedValues = getInitialValues(["fabric", "color"]);
    if (savedValues) {
      this.defaultValues = savedValues;
    }
  }

  async getValues() {
    await this.readyPromise.promise;
    return this.currentValues;
  }

  /**
   * @param {ModelFabric} fabric
   */
  setFabricImage(fabric) {
    const previewContainer = this.fabricRefs.previewContainer();
    if (!previewContainer) {
      throw new Error("Fabric preview container not found");
    }
    previewContainer.style.backgroundImage = `url(${fabric.image})`;
  }

  /**
   * @param {FabricColor} fabricColor
   */
  setColorImage(fabricColor) {
    const previewContainer = this.colorRefs.previewContainer();
    if (!previewContainer) {
      throw new Error("Color preview container not found");
    }
    previewContainer.style.backgroundImage = `url(${fabricColor.image})`;
  }

  /**
   * @param {ModelFabric} modelFabric
   */
  setFabricDescription(modelFabric) {
    const descriptionContainer = this.fabricRefs.descriptionContainer();
    if (!descriptionContainer) {
      throw new Error("No fabric description container found");
    }
    if (modelFabric.description) {
      descriptionContainer.innerHTML = modelFabric.description;
    } else {
      descriptionContainer.innerHTML = "";
    }
  }

  /**
   * @param {FabricColor} fabricColor
   */
  setColorDescription(fabricColor) {
    const descriptionContainer = this.colorRefs.descriptionContainer();
    if (!descriptionContainer) {
      throw new Error("No color description container found");
    }
    if (fabricColor.description) {
      descriptionContainer.innerText = fabricColor.description;
    } else {
      descriptionContainer.innerText = "";
    }
  }

  /**
   * @param {FabricColor} fabricColor
   */
  setColorName(fabricColor) {
    const nameContainer = this.colorRefs.nameContainer();
    if (!nameContainer) {
      throw new Error("No name container found");
    }
    if (fabricColor.name) {
      nameContainer.innerText = fabricColor.name;
    } else {
      nameContainer.innerText = "";
    }
  }

  /**
   * @param {FabricColor} fabricColor
   */
  setColorHiddenProperties(fabricColor) {
    const colorCodeInput = this.colorRefs.colorCode();
    const colorFilterInput = this.colorRefs.colorFilter();
    if (!colorCodeInput || !colorFilterInput) {
      throw new Error("No `_colorCode` and `_colorFilter` inputs found");
    }
    colorCodeInput.value = fabricColor.display_color;
    colorFilterInput.value = fabricColor.filter;
  }

  /**
   * @param {?} err
   */
  handleError(err) {
    console.error(err);
    this.innerText = "Something went wrong. Please try again later.";
    this.readyPromise.reject();
  }
}

if (!customElements.get("fabric-color-selection")) {
  customElements.define("fabric-color-selection", FabricColorSelection);
}
